import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import request from 'lib/request';
import type { ReferenceLocationResponse } from 'types/api-responses';
import type { SurveyRow } from 'components/survey-description';

/**
 * Fills in each survey row's tract polygon.
 *
 * Only the map-pick path adds a row with `location.geometry` — it reads the full
 * `/reference_locations/:id` record. Rows seeded from the saved unit or added
 * from the dropdown carry the location without its polygon. The quarter grid
 * needs one polygon per tract to draw the mock's per-section grid, so this
 * fetches the missing geometry from the same endpoint map-pick uses and merges
 * it in. Rows that already have geometry are left alone, and the panel, preview
 * and save paths (which key on the location id, not its geometry) are unaffected.
 *
 * @param {SurveyRow[]} rows - The current survey rows.
 * @param {Function} setRows - The rows setter.
 */
export const useHydrateSurveyGeometries = (
  rows: SurveyRow[],
  setRows: Dispatch<SetStateAction<SurveyRow[]>>
): void => {
  // One in-flight request per id, cleared when it settles, so a row missing its
  // geometry is fetched exactly once even as `rows` changes on each hydration.
  const inFlight = useRef<Set<number>>(new Set());
  const abort = useRef(new AbortController());
  useEffect(() => {
    const controller = abort.current;
    return () => controller.abort();
  }, []);

  useEffect(() => {
    for (const row of rows) {
      const id = row.location?.id;
      if (
        id === undefined ||
        row.location?.geometry ||
        inFlight.current.has(id)
      ) {
        continue;
      }
      inFlight.current.add(id);
      void (async () => {
        try {
          const response = await request.get<ReferenceLocationResponse>(
            `/reference_locations/${id}`,
            abort.current
          );
          // Aborted (panel closed) or errored — nothing to merge.
          if (!response || 'error' in response || !response.geometry) return;
          const { geometry } = response;
          setRows((current) =>
            current.map((r) => {
              if (r.location?.id !== id || r.location.geometry) return r;
              return { ...r, location: { ...r.location, geometry } };
            })
          );
        } finally {
          inFlight.current.delete(id);
        }
      })();
    }
  }, [rows, setRows]);
};
